import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { withAppHOC } from '../../../hoc';

import PageHeading from '../../../components/PageHeader/PageHeader';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import NoData from '../../../components/NoData/NoData';

import {
  MessageBox,
  Form,
  Fieldset,
  TextInput,
  ResetButton,
  Button,
  Label,
} from '../../../components/FormElements';

import { TableBuilder } from '../../../components/TableElements';
import Pagination from '../../../components/Pagination/Pagination';

import { StyledFiltersContainer } from '../../../components/Styled';

import API from '../../../api';
import { API_RESPONSE_TYPES } from '../../../constants';

import {
  getSearchParams,
  formatCurrency,
  redirectToPageOne,
} from '../../../utils';

import FiltersButton from '../../../components/FiltersButton/FiltersButton';

const AccountsWalletTransactions = (props) => {
  const { id } = useParams();

  // =========================================================
  // Pagination
  // =========================================================
  const [paginationData, setPaginationData] = useState({
    pageNumber: Number(getSearchParams('pageNumber')) || 1,
    recordsPerPage: Number(getSearchParams('recordsPerPage')) || 10,
  });

  // =========================================================
  // Wallet transactions
  // =========================================================
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  // =========================================================
  // Filters
  // =========================================================
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // =========================================================
  // UI state
  // =========================================================
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState('');
  const [responseMessage, setResponseMessage] = useState('');

  // =========================================================
  // Read wallet transactions
  // =========================================================
  const readData = useCallback(
    async (filterStartDate = '', filterEndDate = '') => {
      if (!id) {
        setWalletTransactions([]);
        setTotalRecords(0);
        setResponseStatus(API_RESPONSE_TYPES.FAILURE);
        setResponseMessage('Account ID is missing.');
        return;
      }

      setIsLoading(true);
      setResponseStatus('');
      setResponseMessage('');

      try {
        const requestUrl =
          `/accounts-management/accounts/wallet-transactions/${id}` +
          `?pageNumber=${paginationData.pageNumber}` +
          `&recordsPerPage=${paginationData.recordsPerPage}` +
          `&startDate=${encodeURIComponent(filterStartDate || '')}` +
          `&endDate=${encodeURIComponent(filterEndDate || '')}`;

        console.log('========================================');
        console.log('Wallet Transactions Request');
        console.log('Account ID:', id);
        console.log('Page:', paginationData.pageNumber);
        console.log('Records Per Page:', paginationData.recordsPerPage);
        console.log('Start Date:', filterStartDate);
        console.log('End Date:', filterEndDate);
        console.log('URL:', requestUrl);
        console.log('========================================');

        const response = await API.get(requestUrl);

        console.log('Wallet Transactions API Response:', response.data);

        const responseData = response.data || {};

        const status = responseData.status;
        const message = responseData.message;
        const data = responseData.data;
        const pageInfo = responseData.pageInfo;

        console.log('Wallet Transactions Status:', status);
        console.log('Wallet Transactions Message:', message);
        console.log('Wallet Transactions Data:', data);
        console.log(
          'Wallet Transactions Data Is Array:',
          Array.isArray(data)
        );
        console.log(
          'Wallet Transactions Data Length:',
          Array.isArray(data) ? data.length : 0
        );
        console.log('Wallet Transactions PageInfo:', pageInfo);

        // =====================================================
        // API FAILURE
        // =====================================================
        if (status === API_RESPONSE_TYPES.FAILURE) {
          setWalletTransactions([]);
          setTotalRecords(0);

          setResponseStatus(status);
          setResponseMessage(
            message || 'Unable to load wallet transactions.'
          );

          return;
        }

        // =====================================================
        // Normalize API data
        // =====================================================
        const transactions = Array.isArray(data) ? data : [];

        console.log(
          'Transactions that will be displayed:',
          transactions
        );

        // =====================================================
        // Set state
        // =====================================================
        setWalletTransactions(transactions);

        setTotalRecords(
          Number(pageInfo?.totalRecords) || 0
        );

        // =====================================================
        // Empty page handling
        //
        // If page 2, 3, etc. has no records but totalRecords
        // exists, redirect to page 1.
        // =====================================================
        if (
          transactions.length === 0 &&
          paginationData.pageNumber > 1 &&
          Number(pageInfo?.totalRecords) > 0
        ) {
          console.log(
            'Current page contains no records. Redirecting to page 1.'
          );

          redirectToPageOne();
          return;
        }
      } catch (error) {
        console.error(
          'Accounts Wallet Transactions API Error:',
          error
        );

        setWalletTransactions([]);
        setTotalRecords(0);

        setResponseStatus(API_RESPONSE_TYPES.FAILURE);

        setResponseMessage(
          error?.response?.data?.message ||
          error?.message ||
          'Unable to load wallet transactions.'
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      id,
      paginationData.pageNumber,
      paginationData.recordsPerPage,
    ]
  );

  // =========================================================
  // Initial load / pagination
  // =========================================================
  useEffect(() => {
    readData(startDate, endDate);
  }, [
    readData,
    startDate,
    endDate,
  ]);

  // =========================================================
  // Reload
  // =========================================================
  const reloadData = () => {
    readData(startDate, endDate);
  };

  // =========================================================
  // Apply filter
  // =========================================================
  const filterData = (e) => {
    e.preventDefault();

    setPaginationData((previous) => ({
      ...previous,
      pageNumber: 1,
    }));
  };

  // =========================================================
  // Reset filter
  // =========================================================
  const resetForm = () => {
    setStartDate('');
    setEndDate('');

    setPaginationData((previous) => ({
      ...previous,
      pageNumber: 1,
    }));
  };

  // =========================================================
  // Toggle filters
  // =========================================================
  const onClickShowFilters = () => {
    setShowFilters((previous) => !previous);
  };

  // =========================================================
  // Export wallet transactions
  // =========================================================
  const onClickExport = async () => {
    if (!id) {
      setResponseStatus(API_RESPONSE_TYPES.FAILURE);
      setResponseMessage('Account ID is missing.');
      return;
    }

    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');

    try {
      const requestUrl =
        `/accounts-management/accounts/wallet-transactions-export/${id}` +
        `?startDate=${encodeURIComponent(startDate || '')}` +
        `&endDate=${encodeURIComponent(endDate || '')}`;

      console.log(
        'Wallet Transactions Export Request:',
        requestUrl
      );

      const response = await API.get(requestUrl);

      console.log(
        'Wallet Transactions Export Response:',
        response.data
      );

      const { status, message } = response.data || {};

      if (status === API_RESPONSE_TYPES.FAILURE) {
        setResponseStatus(status);
        setResponseMessage(
          message || 'Unable to export wallet transactions.'
        );
      } else {
        setResponseStatus(status);
        setResponseMessage(
          message || 'Export request submitted successfully.'
        );

        setTimeout(() => {
          setResponseStatus('');
          setResponseMessage('');
        }, 3000);
      }
    } catch (error) {
      console.error(
        'Wallet Transactions Export Error:',
        error
      );

      setResponseStatus(API_RESPONSE_TYPES.FAILURE);

      setResponseMessage(
        error?.response?.data?.message ||
        error?.message ||
        'Unable to export wallet transactions.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // =========================================================
  // Table headings
  // =========================================================
  const tableHeadings = [
    {
      title: 'Date & Time',
      dataSelector: 'createdOn',
      dataType: 'date',
    },

    {
      title: 'Transaction Reference Id',
      dataSelector: 'transactionsReferenceId',
      dataType: 'string',

      CellRenderer: (value) => {
        return value || 'N/A';
      },
    },

    {
      title: 'Transaction Type',
      dataSelector: 'transactionsType',
      dataType: 'string',

      CellRenderer: (value) => {
        return value || 'N/A';
      },
    },

    {
      title: 'Amount',
      dataSelector: 'amount',
      dataType: 'number',
      align: 'center',

      CellRenderer: (value, item) => {
        const transactionType = item?.transactionsType;

        return (
          <span
            style={{
              color:
                transactionType === 'Credit'
                  ? 'forestgreen'
                  : transactionType === 'Debit'
                    ? 'red'
                    : 'inherit',
            }}
          >
            {formatCurrency(value)}
          </span>
        );
      },
    },

    {
      title: 'Balance',
      dataSelector: 'balance',
      dataType: 'number',
      align: 'center',

      CellRenderer: (value) => {
        return formatCurrency(value);
      },
    },

    {
      title: 'Action',
      dataSelector: 'action',
      dataType: 'string',

      CellRenderer: (value) => {
        return value || 'N/A';
      },
    },
  ];

  // =========================================================
  // Render
  // =========================================================
  return (
    <>
      {/* =====================================================
          Page Header
      ====================================================== */}
      <PageHeading
        {...props}
        reloadData={reloadData}
        showExport={true}
        onClickExport={onClickExport}
      />

      {/* =====================================================
          Filter Button
      ====================================================== */}
      <FiltersButton
        onClick={onClickShowFilters}
        value={showFilters}
      />

      {/* =====================================================
          Filters
      ====================================================== */}
      <StyledFiltersContainer
        className={showFilters ? 'show' : ''}
      >
        <Form
          method="POST"
          action="#"
          onSubmit={filterData}
        >
          <Fieldset>
            <Label>Start Date</Label>

            <TextInput
              type="date"
              value={startDate}
              onChange={setStartDate}
              placeholder="Start Date"
              disabled={isLoading}
            />
          </Fieldset>

          <Fieldset>
            <Label>End Date</Label>

            <TextInput
              type="date"
              value={endDate}
              onChange={setEndDate}
              placeholder="End Date"
              disabled={isLoading}
            />
          </Fieldset>

          <Fieldset>
            <Button
              disabled={isLoading}
              type="submit"
            >
              Filter
            </Button>

            <ResetButton
              disabled={isLoading}
              type="button"
              onClick={resetForm}
            >
              Reset
            </ResetButton>
          </Fieldset>
        </Form>
      </StyledFiltersContainer>

      {/* =====================================================
          Response Message
      ====================================================== */}
      {responseStatus && responseMessage && (
        <MessageBox
          status={responseStatus}
          message={responseMessage}
        />
      )}

      {/* =====================================================
          Loading Spinner
      ====================================================== */}
      <LoadingSpinner
        isLoading={
          responseStatus === '' &&
          walletTransactions.length === 0 &&
          isLoading
        }
      />

      {/* =====================================================
          No Data
      ====================================================== */}
      <NoData
        status={
          responseStatus !== API_RESPONSE_TYPES.FAILURE &&
          !isLoading &&
          walletTransactions.length === 0
        }
        message="No wallet transactions found"
      />

      {/* =====================================================
          Wallet Transactions
      ====================================================== */}
      {walletTransactions.length > 0 && (
        <>
          <TableBuilder
            isLoading={
              walletTransactions.length > 0 &&
              isLoading
            }
            tableHeadings={tableHeadings}
            tableData={walletTransactions}
          />

          {/* =================================================
              Pagination
          ================================================== */}
          <Pagination
            totalRecords={totalRecords}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
          />
        </>
      )}
    </>
  );
};

export default withAppHOC(AccountsWalletTransactions);